import React, { useCallback, useRef, useState } from "react";
import { Pressable, StyleSheet, View, type ViewStyle } from "react-native";
import { autoUpdate, flip, offset, shift, useFloating } from "@floating-ui/react";
import { AnimatePresence, MotiView } from "moti";
import { CaretDownIcon, SpeakerSimpleHighIcon } from "@phosphor-icons/react";
import { Portal } from "react-native-paper";

import { useFocusTrap } from "../../../../hooks/useFocusTrap";
import { colors, radii, spacing, typographyScale, webTransitions } from "../../../../styles/design-system";
import { type PlaybackSpeed } from "../../../../utils/toolPreferenceStorage";
import CustomText from "../../../common/general/CustomText/CustomText";
import { ToggleMenuRow, styles } from "./ToolsMenu";

export type VoiceMenuProps = {
  isDisabled: boolean;
  isAudioOn: boolean;
  onAudioOnPress: () => void;
  onAudioOffPress: () => void;
  playbackSpeed: PlaybackSpeed;
  onPlaybackSpeedChange: (speed: PlaybackSpeed) => void;
};

const PLAYBACK_SPEEDS: PlaybackSpeed[] = [0.5, 0.75, 1, 1.25, 1.5, 2];

const voiceStyles = StyleSheet.create({
  menuSectionInset: {
    paddingHorizontal: spacing.xs,
  },
  menuSectionCompact: {
    paddingTop: spacing.xs,
    paddingBottom: spacing.xxs,
  },
  playbackLabel: {
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
    fontWeight: "600",
    color: colors.grey[500],
  },
  playbackButtonRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xxs,
  },
  playbackSpeedButton: {
    flex: 1,
    minHeight: 32,
    borderRadius: radii.sm,
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: spacing.xxs,
    backgroundColor: colors.grey[100],
    ...webTransitions.fast,
  },
  playbackSpeedButtonActive: {
    backgroundColor: colors.brand[500],
  },
  playbackSpeedButtonHovered: {
    backgroundColor: colors.grey[200],
  },
  playbackSpeedButtonPressed: {
    opacity: 0.9,
  },
  playbackSpeedText: {
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
    fontWeight: "600",
    color: colors.grey[600],
  },
  playbackSpeedTextActive: {
    color: colors.white,
  },
});

export const VoiceMenu: React.FC<VoiceMenuProps> = ({
  isDisabled,
  isAudioOn,
  onAudioOnPress,
  onAudioOffPress,
  playbackSpeed,
  onPlaybackSpeedChange,
}) => {
  const [isVoiceMenuOpen, setIsVoiceMenuOpen] = useState(false);

  const { refs, x, y, strategy, placement } = useFloating({
    placement: "top-start",
    middleware: [offset(spacing.xs), flip(), shift({ padding: spacing.sm })],
    whileElementsMounted: autoUpdate,
  });

  const closeVoiceMenu = useCallback(() => {
    setIsVoiceMenuOpen(false);
  }, []);

  const floatingTrapRef = useRef<View | null>(null);
  useFocusTrap(floatingTrapRef, { visible: isVoiceMenuOpen, onEscape: closeVoiceMenu });

  const handleVoiceMenuToggle = useCallback(() => {
    if (isVoiceMenuOpen) {
      closeVoiceMenu();
      return;
    }
    setIsVoiceMenuOpen(true);
  }, [closeVoiceMenu, isVoiceMenuOpen]);

  const handleAutoReadAloudChange = (enabled: boolean) => {
    if (enabled) {
      onAudioOnPress();
      return;
    }

    onAudioOffPress();
  };

  const isVoiceTriggerDisabled = isDisabled && !isVoiceMenuOpen;
  const openUpward = placement.startsWith("top");
  const voiceIconColor = isAudioOn ? colors.brand[600] : colors.grey[600];
  const caretColor = isAudioOn ? colors.brand[400] : colors.grey[400];

  return (
    <View style={styles.toolsMenuContainer} ref={refs.setReference as unknown as React.Ref<View>}>
      <Pressable
        onPress={handleVoiceMenuToggle}
        disabled={isVoiceTriggerDisabled}
        role="button"
        accessibilityLabel={isVoiceMenuOpen ? "Close voice menu" : "Open voice menu"}
        style={(state) => {
          const hovered = (state as { hovered?: boolean }).hovered;
          return [
            styles.toolsTriggerButton,
            isAudioOn && styles.toolsTriggerButtonActive,
            !isVoiceTriggerDisabled && hovered && !isAudioOn && styles.toolsTriggerButtonHovered,
            !isVoiceTriggerDisabled && hovered && isAudioOn && styles.toolsTriggerButtonActiveHovered,
            !isVoiceTriggerDisabled && state.pressed && styles.toolsTriggerButtonPressed,
            isVoiceTriggerDisabled && styles.iconButtonDisabled,
          ];
        }}>
        <SpeakerSimpleHighIcon size={16} color={voiceIconColor} weight="regular" />
        <CustomText
          text="Voice"
          useTranslationText={false}
          style={[styles.toolsTriggerText, isAudioOn && styles.toolsTriggerTextActive]}
        />
        <CaretDownIcon size={12} color={caretColor} weight="bold" />
      </Pressable>

      <Portal>
        {isVoiceMenuOpen ? (
          <Pressable style={styles.toolsMenuOverlay} onPress={closeVoiceMenu} accessibilityLabel="Close voice menu" />
        ) : null}
        <AnimatePresence>
          {isVoiceMenuOpen ? (
            <View
              ref={(node) => {
                (refs.setFloating as unknown as (node: View | null) => void)(node);
                floatingTrapRef.current = node;
              }}
              role="dialog"
              aria-label="Voice menu"
              accessibilityRole="none"
              accessibilityLabel="Voice menu"
              style={[
                styles.toolsMenuPosition,
                {
                  position: (strategy === "fixed" ? "absolute" : strategy) as ViewStyle["position"],
                  left: x ?? 0,
                  top: y ?? 0,
                },
              ]}>
              <MotiView
                key="voice-menu"
                style={styles.toolsMenu}
                from={{
                  opacity: 0,
                  translateY: openUpward ? 8 : -8,
                  scale: 0.98,
                }}
                animate={{ opacity: 1, translateY: 0, scale: 1 }}
                exit={{
                  opacity: 0,
                  translateY: openUpward ? 8 : -8,
                  scale: 0.98,
                }}
                transition={{ type: "timing", duration: 180 }}>
                <View style={styles.menuSection}>
                  <ToggleMenuRow
                    label="Auto Read Aloud"
                    value={isAudioOn}
                    onToggle={handleAutoReadAloudChange}
                    accessibilityLabel="Auto read aloud toggle"
                  />
                </View>

                <View style={styles.menuSeparator} />

                <View style={[styles.menuSection, voiceStyles.menuSectionInset, voiceStyles.menuSectionCompact]}>
                  <CustomText text="Playback Speed" useTranslationText={false} style={voiceStyles.playbackLabel} />
                  <View style={voiceStyles.playbackButtonRow}>
                    {PLAYBACK_SPEEDS.map((speed) => {
                      const isActiveSpeed = playbackSpeed === speed;

                      return (
                        <Pressable
                          key={speed}
                          role="button"
                          onPress={() => onPlaybackSpeedChange(speed)}
                          style={(state) => {
                            const hovered = (state as { hovered?: boolean }).hovered;
                            return [
                              voiceStyles.playbackSpeedButton,
                              isActiveSpeed && voiceStyles.playbackSpeedButtonActive,
                              !isActiveSpeed && hovered && voiceStyles.playbackSpeedButtonHovered,
                              state.pressed && voiceStyles.playbackSpeedButtonPressed,
                            ];
                          }}>
                          <CustomText
                            text={`${speed}x`}
                            useTranslationText={false}
                            style={[
                              voiceStyles.playbackSpeedText,
                              isActiveSpeed && voiceStyles.playbackSpeedTextActive,
                            ]}
                          />
                        </Pressable>
                      );
                    })}
                  </View>
                </View>
              </MotiView>
            </View>
          ) : null}
        </AnimatePresence>
      </Portal>
    </View>
  );
};
